/**
 * Lit la réponse en texte avant de la parser : une erreur PHP, un 404 ou une
 * réponse vide ne sont pas du JSON, et `resp.json()` échouerait alors sur un
 * message illisible. On remonte plutôt le statut HTTP et le début du corps.
 */
const parseJson = async (resp) => {
  const text = await resp.text();
  try {
    return JSON.parse(text);
  } catch (err) {
    const extrait = text.trim().slice(0, 140);
    throw new Error(
      `Réponse inattendue du serveur (HTTP ${resp.status})${extrait ? ` : ${extrait}` : ' : réponse vide'}`
    );
  }
};

/**
 * Questionnaire de satisfaction du formateur.
 *
 * Deux modèles seulement, communs à toutes les formations : un pour le
 * présentiel, un pour la classe virtuelle. Le back choisit le bon d'après le
 * format de la formation de la session.
 *
 * Le parcours est séparé de celui des apprenants (questionnaireApi) parce que
 * le répondant est un intervenant, pas un client.
 */
class FormateurSurveyApi {
  /** Décrit l'envoi sans rien envoyer : modèle retenu, destinataire, réponse déjà reçue. */
  async getPlan(sessionId, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/plan`;
    const resp = await fetch(URL, {
      method: 'POST',
      body: JSON.stringify({ sessionID: sessionId }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Envoie le questionnaire au formateur de la session. */
  async send(sessionId, email, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/send`;
    const resp = await fetch(URL, {
      method: 'POST',
      body: JSON.stringify({ sessionID: sessionId, email }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  // ── Mail de maintien de la formation ──────────────────────────────────────

  /**
   * Le mail de maintien tel qu'il partira : sujet, HTML complet et détail des
   * informations reprises de la fiche session. N'envoie rien.
   */
  async getMaintienPlan(sessionId, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/maintien/preview`;
    const resp = await fetch(URL, {
      method: 'POST',
      body: JSON.stringify({ sessionID: sessionId }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Envoie au formateur la confirmation que la session est maintenue. */
  async sendMaintien(sessionId, email, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/maintien/send`;
    const resp = await fetch(URL, {
      method: 'POST',
      body: JSON.stringify({ sessionID: sessionId, email }),
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Réponses des formateurs d'une session (onglet « Formateurs » du suivi). */
  async getSubmissions(sessionId, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/submissions/${sessionId}`;
    const resp = await fetch(URL, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  // ── Vue transversale : « Résultats des questionnaires », onglet Formateurs ─

  /**
   * Une ligne par réponse formateur, plus les KPI de l'entête. Côté apprenant
   * l'équivalent regroupe par session : ici une session n'a qu'un formateur,
   * il n'y a rien à regrouper.
   */
  async fetchOverview(filters, accessToken, withOptions = false, signal = undefined) {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '' && value !== 'All') {
        params.append(key, value);
      }
    });
    if (withOptions) params.append('with_options', '1');

    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/overview?${params.toString()}`;
    const resp = await fetch(URL, {
      method: 'GET',
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Moyenne et répartition question par question, regroupées par modèle. */
  async fetchSynthesis(filters, accessToken, signal = undefined) {
    const params = new URLSearchParams();
    Object.entries(filters || {}).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '' && value !== 'All') {
        params.append(key, value);
      }
    });

    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/synthesis?${params.toString()}`;
    const resp = await fetch(URL, {
      method: 'GET',
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Une réponse et les questions de son modèle, pour le tiroir de détail. */
  async fetchSubmission(submissionId, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/submission/${submissionId}`;
    const resp = await fetch(URL, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  /** Supprime une réponse formateur. */
  async deleteSubmission(submissionId, accessToken) {
    const URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/submission/${submissionId}`;
    const resp = await fetch(URL, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${accessToken}`,
        'Content-Type': 'application/json',
      },
    });
    return parseJson(resp);
  }

  // ── Accès public, depuis le lien reçu par e-mail ──────────────────────────

  /** Le questionnaire et ses questions, si ce formateur y a droit. */
  async fetchPublic(questionnaireId, intervenantId, sessionId, tenant) {
    let URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/public/${questionnaireId}/${intervenantId}/${sessionId}`;
    if (tenant) {
      URL += `?tenant=${tenant}`;
    }
    const resp = await fetch(URL, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });
    return parseJson(resp);
  }

  /** Enregistre les réponses du formateur. */
  async submit(payload, tenant) {
    let URL = `${process.env.REACT_APP_API_URL}/api/formateur-survey/public/submit`;
    if (tenant) {
      URL += `?tenant=${tenant}`;
    }
    const resp = await fetch(URL, {
      method: 'POST',
      body: JSON.stringify(payload),
      headers: { 'Content-Type': 'application/json' },
    });
    return parseJson(resp);
  }
}

export const formateurSurveyApi = new FormateurSurveyApi();
